import { Fragment, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import elementOuterHeight from 'scripts/utils/elementOuterHeight'

import { Main, Button, Glyph, Span } from './styles'

/**
 *
 * @param {Object} props React Props
 * @param {Object} children React children object
 * @param {Boolean} isOpen set default visibility
 * @param {Number} visibleRows set how many items will be visible
 * @param {String} childrenType set with type <Main> will be polymorphed through styled-component API
 */
const ShowMore = ({
  children,
  childrenType = 'div',
  className,
  isOpen = false,
  scrollToParent = false,
  visibleRows
}) => {
  const MIN_DEFAULT_HEIGHT = 80
  const [isButtonVisible, setButtonVisibility] = useState(false)
  const [isVisible, setIsVisible] = useState(isOpen)
  const [defaultHeight, setDefaultHeight] = useState(MIN_DEFAULT_HEIGHT)
  const [maxHeight, setMaxHeight] = useState(0)
  const mainRef = useRef(null)

  useEffect(() => {
    const getElementOuterHeight = (element) => elementOuterHeight(element) * visibleRows

    const getHeightOnOrientationChange = () => {
      const { current: DOMRef } = mainRef
      const { orientation: { type } } = screen
      const firstElementHeight = getElementOuterHeight(DOMRef.firstElementChild)
      const portraitHeight = firstElementHeight * DOMRef.childElementCount
      const landscapeHeight = portraitHeight / 2

      if (type.includes('landscape')) {
        return setMaxHeight(landscapeHeight)
      }

      return setMaxHeight(portraitHeight)
    }

    const getInitialVisibleHeight = ({ current: mainDOMRef }) => {
      if (visibleRows) {
        if (mainDOMRef.childElementCount && mainDOMRef.childElementCount <= visibleRows) {
          setButtonVisibility(false)
          return setDefaultHeight(`${mainDOMRef.scrollHeight}px`)
        }

        setButtonVisibility(true)
        return setDefaultHeight(`${getElementOuterHeight(mainDOMRef.firstChild)}px`)
      }

      const isRefShorterThanMinDefault = maxHeight < MIN_DEFAULT_HEIGHT

      if (isRefShorterThanMinDefault) {
        setButtonVisibility(false)
        return setDefaultHeight(`${maxHeight}px`)
      }

      setButtonVisibility(true)
      return setDefaultHeight(`${MIN_DEFAULT_HEIGHT}px`)
    }

    setMaxHeight(mainRef.current.scrollHeight)
    getInitialVisibleHeight(mainRef)

    window.addEventListener('orientationchange', getHeightOnOrientationChange)

    return () => {
      window.removeEventListener('orientationchange', getHeightOnOrientationChange)
    }
  }, [maxHeight, visibleRows])

  const onButtonClick = () => {
    setIsVisible(!isVisible)

    if (scrollToParent && isVisible) {
      mainRef.current.parentElement.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
  }

  return (
    <Fragment>
      <Main
        as={childrenType}
        ref={mainRef}
        minHeight={defaultHeight}
        maxHeight={`${maxHeight}px`}
        visible={isVisible}
        isButtonVisible={isButtonVisible}
        className={className}>
        {children}
      </Main>

      {isButtonVisible &&
        <Button
          onClick={onButtonClick}>
          <Span>{isVisible ? 'Ver menos' : 'Ver mais'}</Span>
          <Glyph active={isVisible} className="glyph glyph-arrow-right" />
        </Button>
      }
    </Fragment>
  )
}

ShowMore.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  childrenType: PropTypes.string,
  className: PropTypes.string,
  isOpen: PropTypes.bool,
  scrollToParent: PropTypes.bool,
  visibleRows: PropTypes.number
}

export default ShowMore
